import { usePackageTemplateQuery } from "@/modules/packages/presentation/api/queries";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@devhop/ui";
import { Loader2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useUpdateStudentPackageSelectedClasses } from "../api/queries";

export type InitialSelectedClassInfo = {
  allowedRuleId: string;
  classTypeName: string;
  levelName: string;
  allocatedSessions: number;
  remainingSessions: number;
};

export type EditPackageSelectedClassesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studentId: string;
  packageId: string;
  templateId: string;
  /** ຄ່າເລີ່ມຕົ້ນຈາກ package_template_allowed_rules */
  initialSelectedRuleIds: string[];
  /** ຈຳນວນຄັ້ງຕໍ່ກົດ (allowed rule id) */
  initialClassSessions?: { ruleId: string; sessions: number }[];
  /** ຂໍ້ມູນຄລາດປັจจุบัน — ໃຊ້ກວດການໃຊ້ຄັ້ງແລ້ວ */
  initialSelectedClasses?: InitialSelectedClassInfo[];
  onSuccess?: () => void;
};

function sameRuleSelection(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((id, index) => id === sortedB[index]);
}

export function EditPackageSelectedClassesDialog({
  open,
  onOpenChange,
  studentId,
  packageId,
  templateId,
  initialSelectedRuleIds,
  initialClassSessions,
  initialSelectedClasses,
  onSuccess,
}: EditPackageSelectedClassesDialogProps) {
  const { data: template, isLoading: templateLoading } =
    usePackageTemplateQuery(templateId);
  const mut = useUpdateStudentPackageSelectedClasses(studentId);

  const [selectedRuleIds, setSelectedRuleIds] = useState<string[]>([]);
  const [sessionsByRuleId, setSessionsByRuleId] = useState<
    Record<string, number>
  >({});
  const [transferConfirmOpen, setTransferConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open || !template) return;
    const ids = initialSelectedRuleIds.filter(Boolean);
    setSelectedRuleIds(ids);
    const next: Record<string, number> = {};
    if (initialClassSessions && initialClassSessions.length > 0) {
      for (const row of initialClassSessions) {
        next[row.ruleId] = row.sessions;
      }
    } else if (ids.length > 0) {
      const n = ids.length;
      const per = Math.floor(template.totalSessions / n);
      const rem = template.totalSessions % n;
      ids.forEach((rid, i) => {
        next[rid] = per + (i < rem ? 1 : 0);
      });
    }
    setSessionsByRuleId(next);
    setTransferConfirmOpen(false);
  }, [open, template, initialSelectedRuleIds, initialClassSessions]);

  const maxSelectable = template?.maxSelectableClasses;
  const rules = template?.rules ?? [];
  const canEditStructure = maxSelectable != null && maxSelectable >= 1;

  const totalAllocated = selectedRuleIds.reduce(
    (s, rid) => s + (sessionsByRuleId[rid] ?? 0),
    0,
  );

  const usageSummary = useMemo(() => {
    const rows = initialSelectedClasses ?? [];
    const usedByRule = rows.map((row) => ({
      ...row,
      usedSessions: Math.max(0, row.allocatedSessions - row.remainingSessions),
    }));
    const totalUsed = usedByRule.reduce((sum, row) => sum + row.usedSessions, 0);
    return { usedByRule, totalUsed };
  }, [initialSelectedClasses]);

  const compositionChanged = useMemo(
    () =>
      !sameRuleSelection(
        initialSelectedRuleIds.filter(Boolean),
        selectedRuleIds,
      ),
    [initialSelectedRuleIds, selectedRuleIds],
  );

  const needsTransferConfirm =
    compositionChanged && usageSummary.totalUsed > 0;

  const transferPreview = useMemo(() => {
    if (!needsTransferConfirm || !template) return [];
    const removed = usageSummary.usedByRule.filter(
      (row) => !selectedRuleIds.includes(row.allowedRuleId),
    );
    const added = selectedRuleIds
      .filter((id) => !initialSelectedRuleIds.includes(id))
      .map((id) => rules.find((rule) => rule.id === id))
      .filter((rule): rule is NonNullable<typeof rule> => !!rule);

    return added.map((addedRule, index) => {
      const removedRow =
        removed.find((row) => row.classTypeName === addedRule.classTypeName) ??
        removed[index];
      return {
        fromLabel: removedRow
          ? `${removedRow.classTypeName} - ${removedRow.levelName}`
          : "—",
        fromUsed: removedRow?.usedSessions ?? 0,
        toLabel: `${addedRule.classTypeName} - ${addedRule.levelName}`,
      };
    });
  }, [
    needsTransferConfirm,
    template,
    usageSummary.usedByRule,
    selectedRuleIds,
    initialSelectedRuleIds,
    rules,
  ]);

  const submitUpdate = async (transferUsedSessions: boolean) => {
    await mut.mutateAsync({
      packageId,
      selectedRuleIds,
      classSessions:
        maxSelectable != null && maxSelectable > 1
          ? selectedRuleIds.map((ruleId) => ({
              ruleId,
              sessions: sessionsByRuleId[ruleId] ?? 0,
            }))
          : undefined,
      transferUsedSessions: transferUsedSessions || undefined,
    });
    setTransferConfirmOpen(false);
    onSuccess?.();
    onOpenChange(false);
  };

  const validateAndSubmit = async () => {
    if (!template || !canEditStructure) return;
    if (selectedRuleIds.length !== maxSelectable) {
      toast.error(`ຕ້ອງເລືອກຄລາດສ໌ ${maxSelectable} ກຸ່ມ`);
      return;
    }
    if (maxSelectable > 1 || selectedRuleIds.length > 1) {
      for (const rid of selectedRuleIds) {
        const v = sessionsByRuleId[rid];
        if (v == null || v < 1) {
          toast.error("ກຳນົດຈຳນວນຄັ້ງແຕ່ລະຄລາດສ໌ໃຫ້ຄົບ");
          return;
        }
      }
      if (totalAllocated !== template.totalSessions) {
        toast.error(
          `ຈຳນວນຄັ້ງທັງໝົດຕ້ອງເທົ່າກັບ ${template.totalSessions} ຄັ້ງ`,
        );
        return;
      }
    }

    if (needsTransferConfirm) {
      setTransferConfirmOpen(true);
      return;
    }

    await submitUpdate(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>ແກ້ໄຂຄລາດສ໌ທີ່ໃຊ້ແພັກເກດ</DialogTitle>
          </DialogHeader>

          {templateLoading || !template ? (
            <div className="flex justify-center py-12">
              <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : !canEditStructure || rules.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              ແມ່ແບບນີ້ບໍ່ແຍກຄລາດສ໌ແບບເລືອກຫຼາຍກຸ່ມ — ບໍ່ມີລາຍການກົດທີ່ປ່ຽນໄດ້ທີ່ນີ້.
            </p>
          ) : (
            <div className="space-y-4">
              <p className="text-muted-foreground text-xs">
                ເລືອກ {maxSelectable} ຄລາດສ໌ຈາກລາຍການທີ່ອະນຸຍາດ. ຖ້າປ່ຽນກຸ່ມຄລາດ
                ແລະ ມີການໃຊ້ຄັ້ງແລ້ວ ລະບົບຈະຖາມຢືນຢັນກ່ອນໂອນຈຳນວນທີ່ໃຊ້ໄປຄລາດໃໝ່.
              </p>

              <div className="flex flex-wrap gap-2">
                {rules.map((rule) => {
                  const isChecked = selectedRuleIds.includes(rule.id);
                  const maxSel = maxSelectable ?? 1;
                  const isDisabled =
                    !isChecked && selectedRuleIds.length >= maxSel;
                  return (
                    <Label
                      key={rule.id}
                      htmlFor={`pkg-rule-${rule.id}`}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                        isChecked
                          ? "border-red-600 bg-red-50 dark:bg-red-950/20"
                          : isDisabled
                            ? "cursor-not-allowed opacity-50"
                            : "hover:bg-muted/50"
                      }`}
                    >
                      <Checkbox
                        id={`pkg-rule-${rule.id}`}
                        checked={isChecked}
                        disabled={isDisabled}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedRuleIds((prev) => [...prev, rule.id]);
                          } else {
                            setSelectedRuleIds((prev) =>
                              prev.filter((x) => x !== rule.id),
                            );
                          }
                        }}
                      />
                      <span>
                        {rule.classTypeName} - {rule.levelName}
                      </span>
                    </Label>
                  );
                })}
              </div>

              {maxSelectable != null &&
                maxSelectable > 1 &&
                selectedRuleIds.length === maxSelectable && (
                  <div className="space-y-3 rounded-lg border p-3">
                    <div className="font-medium text-sm">
                      ກຳນົດຈຳນວນຄັ້ງຕໍ່ຄລາດສ໌
                    </div>
                    {selectedRuleIds.map((ruleId) => {
                      const rule = rules.find((x) => x.id === ruleId);
                      if (!rule) return null;
                      return (
                        <div key={ruleId} className="space-y-1">
                          <Label className="text-xs">
                            {rule.classTypeName} - {rule.levelName}
                          </Label>
                          <Input
                            type="number"
                            min={1}
                            className="h-8"
                            value={sessionsByRuleId[ruleId] ?? ""}
                            onChange={(e) => {
                              const n = Number.parseInt(e.target.value, 10);
                              if (!Number.isNaN(n) && n > 0) {
                                setSessionsByRuleId((prev) => ({
                                  ...prev,
                                  [ruleId]: n,
                                }));
                              }
                            }}
                          />
                        </div>
                      );
                    })}
                    <p className="text-muted-foreground text-xs">
                      ລວມ: {totalAllocated} / {template.totalSessions} ຄັ້ງ
                    </p>
                  </div>
                )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              ຍົກເລີກ
            </Button>
            <Button
              onClick={() => void validateAndSubmit()}
              disabled={
                mut.isPending ||
                templateLoading ||
                !template ||
                !canEditStructure
              }
            >
              {mut.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={transferConfirmOpen} onOpenChange={setTransferConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ໂອນຈຳນວນຄັ້ງທີ່ໃຊ້ແລ້ວ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  ແພັກເກດນີ້ໃຊ້ໄປແລ້ວ{" "}
                  <strong>{usageSummary.totalUsed.toLocaleString("lo-LA")}</strong>{" "}
                  ຄັ້ງ. ຕ້ອງການໂອນຈຳນວນທີ່ໃຊ້ໄປຄລາດໃໝ່ບໍ?
                </p>
                {transferPreview.length > 0 && (
                  <ul className="list-disc space-y-1 pl-5">
                    {transferPreview.map((row) => (
                      <li key={row.toLabel}>
                        {row.fromLabel} ({row.fromUsed.toLocaleString("lo-LA")}{" "}
                        ຄັ້ງ) → {row.toLabel}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mut.isPending}>ຍົກເລີກ</AlertDialogCancel>
            <AlertDialogAction
              disabled={mut.isPending}
              onClick={(e) => {
                e.preventDefault();
                void submitUpdate(true);
              }}
            >
              {mut.isPending ? "ກຳລັງບັນທຶກ..." : "ໂອນ ແລະ ບັນທຶກ"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
